import axios from "axios";
import { showTokenExpiryPopUp } from "../components/ui/Toast&Alerts";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  baseUrlImg: process.env.REACT_APP_Img_URL,
});

// Covers every call made through axiosInstance (_api, _get, _post, _put, _delete) in one
// place, instead of each of them handling 401 on their own. If several requests 401 at
// once (e.g. a dashboard firing multiple calls on an already-expired token), only the
// first one shows the popup — the rest are redirecting anyway.
//
// A 401 only means "the session expired" when there WAS a session to begin with — the
// login endpoint itself returns 401 for a plain wrong password (nothing to do with token
// expiry), and any call made before ever logging in has no token either. Both are normal
// and must not trigger this popup.
let sessionExpiredHandled = false;
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const hadToken = !!JSON.parse(localStorage.getItem("permissions") || "null")?.token;
    const isLoginCall = (error.config?.url || "").toLowerCase().includes("auth/login");
    if (error.response?.status === 401 && hadToken && !isLoginCall && !sessionExpiredHandled) {
      sessionExpiredHandled = true;
      await showTokenExpiryPopUp();
      localStorage.removeItem("permissions");
      localStorage.removeItem("permissionsMap");
      window.location.href = "/sign-in";
    }
    return Promise.reject(error);
  }
);

const getAuthHeaders = () => {
  const key = JSON.parse(localStorage.getItem("permissions"));
  return {
   'Authorization': `Bearer ${key?.token || ""}`,
    // 'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJVc2VySWQiOiIxIiwiRnVsbE5hbWUiOiJSZWhhbiIsImVtYWlsIjoicmVoYW5AZXhhbXBsZS5jb20iLCJEZWFsZXJJZCI6IiIsInJvbGUiOiJTYWxlcyBQZXJzb24iLCJSb2xlSWQiOiIxIiwibmJmIjoxNzY3NTg0NDcxLCJleHAiOjE3Njc2NzA4NzEsImlhdCI6MTc2NzU4NDQ3MSwiaXNzIjoiRE1TQVBJIiwiYXVkIjoiRE1TQ2xpZW50cyJ9.HJdFhWjV4d7W8yCODIQmfpohp2gaJqowzezrNWTb2ZY`,
    'Content-Type': "application/json",
  };
};

export const _api = async (method, url, data, callback) => {
  try {
    const config = {
      method,
      url,
      headers: getAuthHeaders(),
    };

    if (method === 'POST' || method === 'PUT') {
      // data.createdBy = 1;
      config.data = data;
    }

    const response = await axiosInstance(config);

    if (response && (response.status === 200 || response.status === 201)) {
      callback(response.data);
      return;
    }

    // fallback error
    callback({ isSuccess: false, message: "Something went wrong" });

  } catch (error) {
    const message = error.response?.data?.message || error.message || 'Unknown error';

    callback({ isSuccess: false, message });
    console.error("API Error:", message);
  }
};



// Downloads a file returned by the API (e.g. a PDF / Excel export) with the auth header.
// Usage --> await _download("CrmRevenueReport/ExportPdf?year=2026", "report.pdf");
export const _download = async (url, fallbackFileName) => {
  const response = await axiosInstance.get(url, {
    headers: getAuthHeaders(),
    responseType: "blob",
  });

  // Prefer the server's file name (Content-Disposition) when it is exposed
  const disposition = response.headers?.["content-disposition"] || "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const fileName = match ? decodeURIComponent(match[1]) : fallbackFileName;

  const blobUrl = URL.createObjectURL(response.data);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
};

// _api('POST', 'PortalUserAPI/Add', postData, (res) => {
//   console.log(res);
// });

// _api('PUT', `PortalUserAPI/UpdatePortalUser/${id}`, updatedData, (res) => {
//   console.log(res);
// });

// _api('GET', 'PortalUserAPI/GetAllUsers', null, (res) => {
//   console.log(res);
// });

// _api('DELETE', `PortalUserAPI/DeleteUser/${id}`, null, (res) => {
//   console.log(res);
// });


export const _get = async (url, callback) => {
  if (url) {
    try {
      await axiosInstance
        .get(url, {
          headers:getAuthHeaders()
        })
        .then(function (response) {
          if (response && response.status === 200) {
            callback(response.data);
          } else {
            console.log("Something wents wrong");
          }
        })
        .catch((error) => {
          console.log(error);
        });
    } catch (error) {
      callback(error);
    }
  }
};

export const _post = async (url, data, callback) => {
  try {
    await axiosInstance
      .post(url, data, {
        headers:getAuthHeaders()
      })
      .then(function (response) {
        if (response && (response.status === 200 || response.status === 201)) {
          callback(response.data);
        } else {
          console.log("Something wents wrong");
        }
      })
      .catch((error) => {
        console.log(error);
      });
  } catch (error) {
    callback(error);
  }
};

export const _put = async (url, data, callback) => {
  try {
    await axiosInstance
      .put(url, data, {
        headers:getAuthHeaders()
      })
      .then(function (response) {
        if (response && (response.status === 200 || response.status === 201)) {
          callback(response.data);
        } else {
          console.log("Something wents wrong");
        }
      })
      .catch((error) => {
        console.log(error);
      });
  } catch (error) {
    callback(error);
  }
};

export const _delete = async (url, id, callback) => {
  try {
    await axiosInstance
      .delete(url, {
        headers:getAuthHeaders()
      })
      .then(function (response) {
        if (response && response.status === 200) {
          callback(response.data);
        } else {
          console.log("Something wents wrong");
        }
      })
      .catch((error) => {
        error(error);
      });
  } catch (error) {
    callback(error);
  }
};